import {
  List,
  ActionIcon,
  Flex,
  Divider,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useContext, memo, useCallback } from "react";
import { IconPlaylistAdd } from "@tabler/icons";
import ActionCard from "src/components/ActionCard";
import NameInputForm, { InputFormElement } from "src/components/NameInputForm";
import { useModals, closeModal } from "@mantine/modals";
import { object, string } from "yup";
import { AppContext } from "src/api/AppContext";
import {
  AlterationAgreementType,
  GetAlterationReasonsQuery,
  useCreateAlterationReasonMutation,
  useDeleteAlterationReasonMutation,
  useUpdateAlterationReasonMutation,
} from "src/generated/graphql";
import {
  CardRadius,
  EMPLOYEE_ROLE_OBJ,
  HeightCard,
  ModalId,
  PATH_NAMES,
} from "src/utils/constants";
import useWritePermission from "src/hooks/usePermission";
import { useParams } from "react-router-dom";
import useTransitionMenu from "src/hooks/useTransitionMenu";

type IReasons = NonNullable<GetAlterationReasonsQuery["alterationReasonMany"]>;

interface ReasonListProps {
  reasons: IReasons;
  onRefetch: () => void;
  locId: string | undefined;
  type: AlterationAgreementType;
  title: string;
}
type ReasonElement = IReasons[0];

const reasonsFormSchema = object().shape({
  name: string()
    .min(5, "Reason should be at least 5 letters")
    .max(400, "Reason must not be greater than 400 letters")
    .required("Reason is required"),
});

function ReasonList({
  reasons,
  onRefetch,
  locId,
  type,
  title,
}: ReasonListProps) {
  const { TransitionMenu, setActiveOption, ShowActivation } =
    useTransitionMenu();
  const {} = useContext(AppContext);
  const { locId: pathId } = useParams<{ locId: string }>();
  const isOrg = pathId !== PATH_NAMES.GENERAL_LOC;
  const isAllowWrite = useWritePermission(
    isOrg ? EMPLOYEE_ROLE_OBJ.orgSettings : EMPLOYEE_ROLE_OBJ.locSettings
  );
  const theme = useMantineTheme();
  const modals = useModals();
  let color = theme.colors.secondaryTextColor[6];

  const [] = useCreateAlterationReasonMutation();
  const [createAlterationReason, { loading: createLoading }] =
    useCreateAlterationReasonMutation({
      onCompleted: () => {
        onRefetch();
        closeModal(ModalId.AddReason);
      },
    });
  const [updateAlterationReason, { loading: updateLoading }] =
    useUpdateAlterationReasonMutation({
      onCompleted: () => {
        onRefetch();
        closeModal(ModalId.EditReason);
      },
    });
  const [deleteAlterationReason] = useDeleteAlterationReasonMutation({
    onCompleted: () => {
      onRefetch();
      closeModal(ModalId.DeleteTransition);
    },
  });

  const handleOpenListMenu = (option: ReasonElement) => {
    setActiveOption(option);
  };

  const handleSubmission = (reason: string, id?: string) => {
    const obj = {
      reason,
      location: {
        id: locId!,
      },
      type,
    };

    if (id) {
      updateAlterationReason({
        variables: {
          data: obj,
          updateAlterationReasonId: id,
        },
      });
    } else {
      createAlterationReason({
        variables: {
          data: obj,
        },
      });
    }
  };

  const onCreate = useCallback(() => {
    modals.openModal({
      modalId: ModalId.AddReason,
      closeOnClickOutside: false,
      title: `Create ${title}`,
      children: (
        <NameInputForm
          schema={reasonsFormSchema}
          onSubmit={(values) => {
            handleSubmission(values.name);
          }}
          loading={createLoading}
          onCancel={() => closeModal(ModalId.AddReason)}
          type="Reason"
          inputType={InputFormElement.TEXTAREA}
        />
      ),
    });
  }, [title]);

  const onEdit = useCallback(({ id, reason }: ReasonElement) => {
    modals.openModal({
      modalId: ModalId.EditReason,
      closeOnClickOutside: false,
      title: `Edit ${title}`,
      children: (
        <NameInputForm
          schema={reasonsFormSchema}
          onSubmit={(values) => {
            handleSubmission(values.name, id);
          }}
          loading={updateLoading}
          value={reason!}
          onCancel={() => closeModal(ModalId.EditReason)}
          type="Reason"
          inputType={InputFormElement.TEXTAREA}
        />
      ),
    });
  }, []);

  const onDelete = (id: string) => {
    deleteAlterationReason({
      variables: { deleteAlterationReasonId: id },
    });
  };

  const onUpdateActivation = (id: string, isActive: boolean) => {
    updateAlterationReason({
      variables: { updateAlterationReasonId: id, data: { isActive } },
    });
  };

  return (
    <ActionCard
      title={title}
      rightIconButton={
        isAllowWrite ? (
          <ActionIcon
            size="lg"
            onClick={() => onCreate()}
            sx={{
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
          >
            <IconPlaylistAdd color={theme.colors.primaryTextColor[6]} />
          </ActionIcon>
        ) : (
          <></>
        )
      }
      height={HeightCard.sm}
      radius={CardRadius.sm}
      size="md"
    >
      {!reasons.length ? (
        <Flex justify="center" align="center">
          <Text color={color}>Click</Text>&nbsp;
          {
            <ActionIcon
              size="lg"
              onClick={() => onCreate()}
              disabled={!isAllowWrite}
              sx={{
                "&:hover": {
                  backgroundColor: theme.colors.secondary[4],
                },
              }}
            >
              <IconPlaylistAdd color={theme.colors.primaryTextColor[6]} />
            </ActionIcon>
          }
          &nbsp;
          <Text color={color}>to add a reason</Text>
        </Flex>
      ) : (
        <List w="100%" listStyleType="none" style={{ color }}>
          {reasons.map((item, index) => {
            return (
              <List.Item
                w="100%"
                icon={null}
                key={item.id}
                c={color}
                sx={{
                  "&": {
                    position: "relative",
                  },
                  "& ::marker": {
                    content: "none",
                  },
                  "& .mantine-List-itemWrapper": {
                    display: "flex",
                  },
                  "& .mantine-List-itemWrapper > span > div": {
                    alignItems: "center",
                  },
                }}
              >
                {!!index && (
                  <Divider style={{ borderColor: theme.colors.gray[2] }} />
                )}

                <Flex
                  align="center"
                  justify="space-between"
                  sx={{
                    padding: "4px 0",
                  }}
                >
                  <Text size={13} fw={500} sx={{ width: "40vw" }}>
                    {item.reason}
                  </Text>

                  {ShowActivation(item.isActive)}

                  {isAllowWrite && (
                    <TransitionMenu
                      onClick={() => handleOpenListMenu(item)}
                      onEdit={() => onEdit(item)}
                      onDelete={onDelete}
                      id={item.id}
                      isActive={item.isActive}
                      onUpdateActivation={onUpdateActivation}
                    />
                  )}
                </Flex>
              </List.Item>
            );
          })}
        </List>
      )}
    </ActionCard>
  );
}

export default memo(ReasonList);
